import {
  File,
  FileText,
  Image,
  Presentation,
  RefreshCw,
  RotateCcw,
  Sheet,
  Video,
  X,
} from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useFileByGuid } from "@/hooks/useFileByGuid";
import { cn } from "@/lib/utils";

export interface EditableFile {
  id: number;
  guid: string;
  mime_type: string;
  file_name: string;
}

interface ExistingFileAttachmentProps {
  files: EditableFile[];
  filesToRemove: Set<number>;
  filesToReplace: Map<number, File>;
  onRemove: (fileId: number) => void;
  onReplace: (fileId: number, newFile: File) => void;
}

const isImageMimeType = (mimeType: string) => mimeType.startsWith("image/");

const getFileIconByName = (fileName: string) => {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext))
    return <Image size={24} className="text-blue-500" />;
  if (["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv"].includes(ext))
    return <Video size={24} className="text-purple-500" />;
  if (ext === "pdf") return <FileText size={24} className="text-red-500" />;
  if (["xlsx", "xls", "csv"].includes(ext))
    return <Sheet size={24} className="text-green-500" />;
  if (["pptx", "ppt"].includes(ext))
    return <Presentation size={24} className="text-orange-500" />;
  if (["docx", "doc"].includes(ext))
    return <FileText size={24} className="text-blue-600" />;
  if (["txt", "md", "rtf"].includes(ext))
    return <FileText size={24} className="text-gray-600" />;
  return <File size={24} className="text-gray-500" />;
};

const truncateFileName = (fileName: string, maxLength = 10): string => {
  if (fileName.length <= maxLength) return fileName;
  const dotIndex = fileName.lastIndexOf(".");
  const extension = dotIndex !== -1 ? fileName.substring(dotIndex + 1) : "";
  const nameWithoutExt =
    dotIndex !== -1 ? fileName.substring(0, dotIndex) : fileName;
  const truncatedName = nameWithoutExt.substring(0, maxLength - 3);
  return `${truncatedName}...${extension ? `.${extension}` : ""}`;
};

interface FileChipProps {
  file: EditableFile;
  isRemoved: boolean;
  replacedWith: File | undefined;
  onRemove: (fileId: number) => void;
  onReplace: (fileId: number, newFile: File) => void;
}

const FileChip: React.FC<FileChipProps> = ({
  file,
  isRemoved,
  replacedWith,
  onRemove,
  onReplace,
}) => {
  const { t } = useTranslation();
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const isImage = isImageMimeType(file.mime_type);
  const { fileBlobUrl } = useFileByGuid(
    isImage && !replacedWith ? file.guid : null,
  );

  const replacedPreviewUrl = useMemo(() => {
    if (!replacedWith || !replacedWith.type.startsWith("image/")) return null;
    return URL.createObjectURL(replacedWith);
  }, [replacedWith]);

  useEffect(() => {
    return () => {
      if (replacedPreviewUrl) URL.revokeObjectURL(replacedPreviewUrl);
    };
  }, [replacedPreviewUrl]);

  const displayName = replacedWith
    ? truncateFileName(replacedWith.name)
    : truncateFileName(file.file_name);

  const handleReplaceClick = () => {
    replaceInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      onReplace(file.id, selected);
    }
    e.target.value = "";
  };

  return (
    <div
      className={cn(
        "relative h-20 w-[180px] rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-shadow hover:shadow-md",
        isRemoved && "opacity-40",
      )}
    >
      <div className="flex h-full items-start gap-3">
        <div className="mt-1 shrink-0">
          {replacedPreviewUrl ? (
            <img
              src={replacedPreviewUrl}
              alt={replacedWith?.name}
              className="size-12 rounded object-cover"
            />
          ) : replacedWith ? (
            getFileIconByName(replacedWith.name)
          ) : isImage && fileBlobUrl ? (
            <img
              src={fileBlobUrl}
              alt={file.file_name}
              className="size-12 rounded object-cover"
            />
          ) : (
            getFileIconByName(file.file_name)
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-gray-900">
            {displayName}
          </div>
          {isRemoved && (
            <div className="mt-1 text-xs text-red-500">
              {t("common.fileRemoved")}
            </div>
          )}
          {replacedWith && !isRemoved && (
            <div className="mt-1 text-xs text-blue-500">
              {t("common.fileReplaced")}
            </div>
          )}
        </div>
      </div>

      {/* Remove / Undo remove button */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute -right-2 -top-2 z-10 size-6 rounded-full border border-gray-300 bg-white shadow-sm transition-all duration-200 hover:scale-110 hover:border-red-300 hover:bg-red-50"
        onClick={() => onRemove(file.id)}
        aria-label={
          isRemoved ? t("common.undoFileRemoval") : t("common.removeFile")
        }
      >
        {isRemoved ? (
          <RotateCcw size={12} className="text-gray-600" />
        ) : (
          <X size={12} className="text-gray-600 hover:text-red-500" />
        )}
      </Button>

      {/* Replace button */}
      {!isRemoved && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute -bottom-2 -right-2 z-10 size-6 rounded-full border border-gray-300 bg-white shadow-sm transition-all duration-200 hover:scale-110 hover:border-blue-300 hover:bg-blue-50"
          onClick={handleReplaceClick}
          aria-label={t("common.replaceFile")}
        >
          <RefreshCw size={12} className="text-gray-600 hover:text-blue-500" />
        </Button>
      )}

      <input
        type="file"
        ref={replaceInputRef}
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
};

export const ExistingFileAttachment: React.FC<ExistingFileAttachmentProps> = ({
  files,
  filesToRemove,
  filesToReplace,
  onRemove,
  onReplace,
}) => {
  if (files.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-3">
      {files.map((file) => (
        <FileChip
          key={file.id}
          file={file}
          isRemoved={filesToRemove.has(file.id)}
          replacedWith={filesToReplace.get(file.id)}
          onRemove={onRemove}
          onReplace={onReplace}
        />
      ))}
    </div>
  );
};
